import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import {
  useFlightAnalysisResult,
  EngineStartupEvent,
  EngineStartupCheck,
  isStartupError,
} from '../../hooks/useFlightAnalysisResults';
import { VerdictChip } from './VerdictChip';
import { VERDICT_COLORS } from './verdictColors';

/**
 * Sub-component that fetches and displays analysis for a single flight.
 *
 * Addressed by the flight's `object_id` — the `fleet.file_objects` row — never
 * by its storage key (STD-029 R-14/D-8). The caller renders this only for a
 * flight that has one; a flight without a registry row has no verdict surface
 * at all, exactly as it has no badge in the grid (R-9).
 */
export const FlightAnalysisSection: React.FC<{
  objectId: string;
  labelStyle: Record<string, unknown>;
}> = ({ objectId, labelStyle }) => {
  const [expanded, setExpanded] = useState(false);
  // Only fetch when expanded — avoids N parallel heavy queries on dialog open
  const { data: analysis, loading } = useFlightAnalysisResult(expanded ? objectId : null);

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
        }}
      >
        <Typography variant="caption" sx={{ ...labelStyle }}>
          Flight Analysis {expanded ? '▾' : '▸'}
        </Typography>
      </Box>

      {expanded && loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Loading analysis...
          </Typography>
        </Box>
      )}

      {expanded && !loading && !analysis && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          No analysis available for this flight.
        </Typography>
      )}

      {expanded &&
        analysis &&
        (() => {
          const checkResults = analysis.check_results as Record<string, unknown> | null;
          const engineStartup = checkResults?.engineStartup as {
            events?: EngineStartupEvent[];
          } | null;
          const events = engineStartup?.events ?? [];
          const statistics = checkResults?.statistics as Record<string, unknown> | null;
          const fft = checkResults?.fft as Record<string, unknown> | null;

          return (
            <>
              {/* Section 1: Automated Analysis */}
              <Typography variant="caption" sx={{ ...labelStyle, display: 'block', mt: 1, mb: 1 }}>
                Automated Analysis
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  mb: 1.5,
                  border: `1px solid ${alpha(
                    VERDICT_COLORS[analysis.overall_verdict] || '#9e9e9e',
                    0.3
                  )}`,
                  backgroundColor: alpha(
                    VERDICT_COLORS[analysis.overall_verdict] || '#9e9e9e',
                    0.03
                  ),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: events.length ? 1.5 : 0,
                  }}
                >
                  <VerdictChip verdict={analysis.overall_verdict} />
                  <Typography variant="body2" color="text.secondary">
                    {analysis.file_type} analysis
                    {analysis.analysis_completed_at
                      ? ` — ${new Date(analysis.analysis_completed_at).toLocaleDateString()}`
                      : ''}
                  </Typography>
                </Box>

                {/* Engine startup events */}
                {events.map((event, idx) => {
                  if (isStartupError(event)) {
                    return (
                      <Box key={idx} sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VerdictChip verdict="fail" />
                          <Typography variant="body2" color="error.main">
                            Startup Event {idx + 1}: {event.error}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }

                  return (
                    <Box key={idx} sx={{ mb: idx < events.length - 1 ? 1.5 : 0 }}>
                      {events.length > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Startup Event {idx + 1}
                          </Typography>
                          <VerdictChip verdict={event.overall_verdict} />
                        </Box>
                      )}
                      {(event.checks ?? []).map((check: EngineStartupCheck) => (
                        <Box
                          key={check.check_id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            py: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VerdictChip verdict={check.verdict} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {check.name}
                            </Typography>
                            {check.value !== null && (
                              <Typography variant="body2" color="text.secondary">
                                ={' '}
                                {typeof check.value === 'number'
                                  ? check.value.toFixed(2)
                                  : check.value}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5, mt: 0.25 }}
                          >
                            {check.details}
                            {check.threshold ? ` (threshold: ${check.threshold})` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })}

                {events.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No engine startup checks available
                    {statistics ? ` — ${Object.keys(statistics).length} columns analyzed` : ''}
                    {fft ? `, FFT on ${Object.keys(fft).length} columns` : ''}
                  </Typography>
                )}
              </Box>

              {/* Section 2: LLM Interpretation */}
              {analysis.agent_interpretation && (
                <>
                  <Typography variant="caption" sx={{ ...labelStyle, display: 'block', mb: 1 }}>
                    AI Interpretation{analysis.agent_model ? ` (${analysis.agent_model})` : ''}
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.2)}`,
                      backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02),
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem', color: 'text.secondary' }}
                    >
                      {analysis.agent_interpretation}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          );
        })()}
    </Box>
  );
};
